import React, {useState, useEffect, useRef} from 'react';
import {Box, Tab, Tabs} from '@mui/material';
import {getKeyFromPath, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import cookie from 'cookie';

interface StickyJobNavigationProps {
  sections: Array<{
    id: string;
    label: string;
    ref: React.RefObject<HTMLElement>;
    tag: string;
  }>;
  onTabClick?: (sectionId: string) => void;
  templateInfo?: {
    templateNo?: string;
    origin?: string;
  };
  jobInfo?: any;
}
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const StickyJobNavigation: React.FC<StickyJobNavigationProps> = ({
  sections,
  onTabClick,
  templateInfo,
  jobInfo,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isSticky, setIsSticky] = useState(false);
  const [showHiristPromotionBanner, setShowHiristPromotionBanner] =
    useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const originalOffsetTop = useRef<number>(0);
  const router = useRouter();

  useEffect(() => {
    if (navRef.current) {
      originalOffsetTop.current = navRef.current.offsetTop;
    }
  }, []);

  useEffect(() => {
    // Coalesce scroll processing into a single rAF so that all layout reads
    // (scrollTop + each section's getBoundingClientRect) happen in one batch
    // just before paint, instead of interleaving with React's DOM writes on
    // every scroll event. This avoids forced reflow / layout thrashing.
    let rafId: number | null = null;

    const processScroll = () => {
      rafId = null;

      // --- read phase: all layout reads grouped together ---
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const threshold = showHiristPromotionBanner ? 240 : 150;
      let currentSection = 0;
      for (let index = 0; index < sections.length; index++) {
        const el = sections[index].ref.current;
        if (el && el.getBoundingClientRect().top <= threshold) {
          currentSection = index;
        }
      }

      // --- write phase: React state updates (bail out if unchanged) ---
      if (navRef.current) {
        // Add buffer to delay sticky behavior
        setIsSticky(
          scrollTop > originalOffsetTop.current + (isAppIimjobs ? 0 : -220)
        );
      }
      setActiveTab(currentSection);
    };

    const handleScroll = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(processScroll);
      }
    };

    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [sections, showHiristPromotionBanner]);
  const smoothScrollTo = (offsetTop: number, duration = 1000) => {
    const start = window.scrollY;
    const distance = offsetTop - start;
    let startTime: number;

    const scroll = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1); // Limit progress to 100%

      window.scrollTo(0, start + distance * percentage);

      if (progress < duration) {
        requestAnimationFrame(scroll); // Continue scrolling
      }
    };

    requestAnimationFrame(scroll); // Start the scroll animation
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    TrackEventUBA('jdPageClick', 'click', {
      ref: router.query.ref || '',
      pageName: 'Job Description Page',
      jobID: jobInfo?.id,
      jobVersion: '',
      jobRef: getKeyFromPath('ref') || '',
      jobPos: router.query.jobPos || '',
      page: 'JD',
      section: `${sections[newValue]?.tag}`,
      sessionUsername: sessionStorage.getItem('userId') || '',
      typeClick: `${sections[newValue]?.tag}-Tab`,
      companyID: jobInfo?.companyData?.companyId,
      centralCompanyID: jobInfo?.recruiter?.centralizedCompanyId,
      companyName: jobInfo?.companyData?.companyName,
      templateID: templateInfo?.templateNo,
      isBranded: 'Yes',
    });
    const section = sections[newValue];

    if (section.ref.current) {
      const offsetTop =
        section.id === 'job-description'
          ? 0
          : section.ref.current.offsetTop -
            (isAppIimjobs ? 140 : showHiristPromotionBanner ? 235 : 145);
      smoothScrollTo(offsetTop, 700);
    }

    if (onTabClick) {
      onTabClick(section.id);
    }
  };
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!isAppIimjobs) {
        const cookies = cookie.parse(document?.cookie);
        const myCookieValue = cookies['linkage_banner'];
        if (myCookieValue !== '1') {
          setShowHiristPromotionBanner(true);
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleBannerClosed = () => {
      setShowHiristPromotionBanner(false);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('hiristBannerClosed', handleBannerClosed);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('hiristBannerClosed', handleBannerClosed);
      }
    };
  }, []);

  return (
    <Box
      ref={navRef}
      sx={{
        position: isSticky ? 'fixed' : 'relative',
        top: isSticky
          ? isAppIimjobs
            ? 50
            : showHiristPromotionBanner
              ? 140
              : 50
          : 'auto',
        left: 0,
        right: 0,
        height: isSticky ? 52 : 40,
        pt: isSticky ? 1.5 : 0,
        zIndex: isSticky ? 1000 : 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: isSticky ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
        transition: 'all 0.3s ease-in-out',
        width: '100%',
        backgroundColor: isAppIimjobs ? '#FFF' : '#F2F5FA',
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          margin: '0 auto',
          px: {xs: isAppIimjobs ? 2 : 1, md: 3},
          pr: isAppIimjobs ? '16px' : 0,
          height: 40,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 1.5,
              py: 0,
              paddingBottom: 0.25,
              fontSize: isAppIimjobs ? '12px' : '14px',
              fontWeight: isAppIimjobs ? 500 : 600,
              textTransform: 'none',
              color: '#7E7E7E',
              '&.Mui-selected': {
                color: '#333',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#333',
              height: 2,
              borderRadius: '16px 16px 0px 0px',
              mt: -8,
            },
            '& .MuiTabs-scrollButtons': {
              '&.Mui-disabled': {
                opacity: 0.3,
              },
            },
            '& .MuiTabs-scroller': {
              height: 40,
            },
          }}
        >
          {sections.map((section, index) => (
            <Tab
              disableRipple
              key={section.id}
              label={section.label}
              id={`nav-tab-${index}`}
              aria-controls={`nav-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  );
};

export default StickyJobNavigation;
